import "assets/scss/components/calendar.scss";
import React, { useState, useEffect } from "react";
import { useApp } from "context";
import { Helpers } from "utils";
import { Week } from "components/datetime/Week";
import { IconButton } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import TodayIcon from "@mui/icons-material/Today";

export type Item = {
  day: number;
  today: boolean;
  selected: boolean;
  disabled: boolean;
};

type Props = {
  min?: Date;
  max?: Date;
  value?: Date;
  locale?: string;
  weekend?: boolean;
  disabled?: boolean;
  rules?: any;
  exceptions?: any;
  onSelect?: (value: Date) => void;
};

export const Calendar = (props: Props) => {
  const { theme } = useApp();

  const today = new Date();
  const currentDate = props.value || today;

  const [days, setDays] = useState<Item[][]>([]);
  const [selected, setSelected] = useState<Date>(currentDate);
  const [day, setDay] = useState<number>(currentDate.getDate());
  const [month, setMonth] = useState<number>(currentDate.getMonth());
  const [year, setYear] = useState<number>(currentDate.getFullYear());

  useEffect(() => {
    config();
  }, [props.disabled, selected, month]);

  const monthName = new Date(day, month, 1).toLocaleString(props.locale, {
    month: "long",
  });

  const weekday = props.weekend ? [0, 6] : [1, 5];

  const calculateWeekdays = () => {
    const weekdays = [];
    const date = new Date();
    while (date.getDay() !== weekday[0]) date.setDate(date.getDate() - 1);
    for (let i = weekday[0]; i <= weekday[1]; i++) {
      weekdays.push(date.toLocaleString(props.locale, { weekday: "narrow" }));
      date.setDate(date.getDate() + 1);
    }
    return weekdays;
  };

  const calculateDays = () => {
    const calculatedDays = [];
    const from = new Date(year, month, 1);
    const to = new Date(year, month + 1, 0);
    while (to.getDay() !== weekday[1]) to.setDate(to.getDate() + 1);
    while (from.getDay() !== weekday[0]) from.setDate(from.getDate() - 1);
    while (from < to || Helpers.compareTo(from, to)) {
      const wd = from.getDay();
      if (wd >= weekday[0] && wd <= weekday[1]) {
        calculatedDays.push(new Date(from));
      }
      from.setDate(from.getDate() + 1);
    }
    return calculatedDays;
  };

  const hasBackward = (() => {
    const first = new Date(year, month, 1);
    return props.min
      ? first > props.min || Helpers.compareTo(first, props.min)
      : true;
  })();

  const hasForward = (() => {
    const last = new Date(year, month, 0);
    return props.max
      ? last < props.max || Helpers.compareTo(last, props.max)
      : true;
  })();

  const config = () => {
    const items: Item[][] = [];

    for (const day of calculateDays()) {
      const item = {} as Item;
      item.day = day.getDate();
      item.disabled =
        props.disabled ||
        day.getMonth() !== month ||
        !Helpers.inRange(day, props.min, props.max) ||
        isException(day);

      if (!item.disabled) {
        item.disabled = false;
        if (Helpers.compareTo(day, today)) item.today = true;
        item.selected = !!(selected && Helpers.compareTo(day, selected));
      }

      if (day.getDay() === weekday[0]) {
        items.push([item]);
      } else {
        items[items.length - 1].push(item);
      }
    }
    setDays(items);
  };

  const isException = (date: Date) => {
    let exception = false,
      found = false;
    props.exceptions &&
      Object.keys(props.exceptions).forEach((item: string) => {
        if (Helpers.compareTo(new Date(item), date)) {
          found = true;
          const values: { start: number; end: number } = props.exceptions[item];
          if (values.start === 0) {
            exception = true;
          }
          return false;
        }
      });

    if (!found) {
      const values: { start: number; end: number } = props.rules[date.getDay()];
      return values.start === 0;
    }
    return exception;
  };

  const handleSelect = (day: number) => {
    handleUnselect();
    const selected = new Date(year, month, day);
    setSelected(selected);
    props.onSelect && props.onSelect(selected);
  };

  const handleSelectToday = () => {
    setSelected(today);
    setDay(today.getDate());
    setMonth(today.getMonth());
    setYear(today.getFullYear());
    handleSelect(today.getDate());
  };

  const handleUnselect = () => {
    if (
      selected !== null &&
      selected.getMonth() === month &&
      selected.getFullYear() === year
    ) {
      const day = selected.getDate();
      [].concat.apply([], days as any).forEach((item: Item) => {
        if (item.day === day) {
          item.selected = false;
          return false;
        }
      });
    }
  };

  const handleClick = (date: Item) => handleSelect(date.day);

  const handleArrowClick = (value: number) => {
    let m,
      y = year;
    if (month + value < 0) {
      m = 11;
      y--;
    } else if (month + value > 11) {
      m = 0;
      y++;
    } else {
      m = month + value;
    }
    setMonth(m);
    setYear(y);
  };

  return (
    <div
      className="qh-calendar"
      style={{ backgroundColor: theme.palette.background.color }}
    >
      <div className="qh-header">
        <div className="qh-current">
          <div className="qh-month">{monthName}</div>
          <div className="qh-year">{year}</div>
        </div>
        <div className="qh-control" style={{ color: theme.palette.font.color }}>
          <IconButton
            color="inherit"
            disabled={!hasBackward || props.disabled}
            onClick={() => handleArrowClick(-1)}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton
            color="inherit"
            disabled={!hasForward || props.disabled}
            onClick={() => handleArrowClick(1)}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
          <IconButton
            color="inherit"
            disabled={props.disabled}
            onClick={handleSelectToday}
          >
            <TodayIcon />
          </IconButton>
        </div>
      </div>
      <div className="qh-content">
        <div className="qh-weekday">
          {calculateWeekdays().map((item: string, i: number) => {
            return (
              <div
                className="qh-item"
                key={i}
                style={{ color: theme.palette.font.color }}
              >
                {item}
              </div>
            );
          })}
        </div>
        <div className="qh-month">
          {days.map((item: Item[], i: number) => {
            return <Week key={i} weeks={item} onClick={handleClick} />;
          })}
        </div>
      </div>
    </div>
  );
};
